import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUserAuth } from "../../contexts/UserAuthContext";
import { useUserProfile } from "../../contexts/UserProfileContext";
import { usePublicPages } from "../../contexts/PublicPagesContext";
import {
  getProfileTabFromPathname,
  isOwnOnlyProfileTab,
  isValidProfileTab,
} from "../../utils/profilePaths";
import UserProfilePostsTab from "./tabs/UserProfilePostsTab";
import UserProfileSetupTab from "./tabs/UserProfileSetupTab";
import MatchesPage from "./sections/MatchesPage";
import FavoritesPage from "./sections/FavoritesPage";
import CollectionsPage from "./sections/CollectionsPage";
import ActivityPage from "./sections/ActivityPage";
import RecentlyDeletedPosts from "./sections/RecentlyDeletedPosts";
import NotificationsPage from "./sections/NotificationsPage";
import SettingsPage from "./sections/SettingsPage";
import CommunityPage from "./sections/CommunityPage";

const TAB_RENDERERS = {
  posts: () => <UserProfilePostsTab />,
  setup: () => <UserProfileSetupTab />,
  matches: () => <MatchesPage />,
  favorites: () => <FavoritesPage />,
  saved: () => <CollectionsPage />,
  activity: () => <ActivityPage />,
  "recently-deleted": () => <RecentlyDeletedPosts />,
  notifications: () => <NotificationsPage />,
  settings: () => <SettingsPage />,
  messages: () => <CommunityPage forcedSub="messages" />,
  requests: () => <CommunityPage forcedSub="requests" />,
  reviews: () => <CommunityPage forcedSub="reviews" />,
  answers: () => <CommunityPage forcedSub="answers" />,
  comments: () => <CommunityPage forcedSub="comments" />,
};

const FEED_ONLY_PROFILE_TABS = new Set([
  "posts",
  "recently-deleted",
  "answers",
  "comments",
]);

/**
 * Renders profile tab panels inside the layout shell (no Outlet) so the header
 * and sidebar stay mounted. Visited tabs stay in the DOM for instant switching.
 */
const UserProfileTabContent = () => {
  const { pathname } = useLocation();
  const { userId, isOwnProfile } = useUserProfile();
  const { user } = useUserAuth();
  const { isPageEnabled } = usePublicPages();
  const feedEnabled = isPageEnabled("feed");
  const activeTab = getProfileTabFromPathname(pathname, userId);

  const [mountedTabs, setMountedTabs] = useState(() => new Set([activeTab]));

  useEffect(() => {
    setMountedTabs((prev) => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);

  if (!isValidProfileTab(activeTab)) {
    return <Navigate to={feedEnabled ? `/user/${userId}` : `/user/${userId}/setup`} replace />;
  }

  if (!feedEnabled && FEED_ONLY_PROFILE_TABS.has(activeTab)) {
    return <Navigate to={`/user/${userId}/setup`} replace />;
  }

  if (isOwnOnlyProfileTab(activeTab)) {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (!isOwnProfile) {
      return <Navigate to={feedEnabled ? `/user/${userId}` : `/user/${userId}/setup`} replace />;
    }
  }

  return (
    <>
      {[...mountedTabs].map((tab) => {
        if (!isValidProfileTab(tab)) return null;
        if (!feedEnabled && FEED_ONLY_PROFILE_TABS.has(tab)) return null;
        if (isOwnOnlyProfileTab(tab) && !isOwnProfile) return null;

        const render = TAB_RENDERERS[tab];
        if (!render) return null;

        return (
          <div
            key={tab}
            style={{ display: activeTab === tab ? "block" : "none" }}
            aria-hidden={activeTab !== tab}
          >
            {render()}
          </div>
        );
      })}
    </>
  );
};

export default UserProfileTabContent;
